import { API_BASE_URL } from '../../common/api/apiConfig';
import { Item } from '../../common/types';

export interface AddItemRequest {
	user_uid: string;
	type_label_long: string;
	size_label: string;
	brand_code: string;
	firestore_id?: string; // Optional - for type-only items without specific product
	fitting_code: number;
	fitting_measurements: number[];
	color_code?: string;
}

export interface AddItemResponse {
	success: boolean;
	itemId?: string;
	image_url?: string | null;
	message?: string;
}

export const addItem = async (userUid: string, item: Item, authToken: string): Promise<AddItemResponse> => {
	try {
		console.debug('[AddItem] Input parameters:', { userUid, authToken: authToken ? `${authToken.substring(0, 20)}...` : 'EMPTY' });
		console.debug('[AddItem] Input item object:', item);
		console.debug('[AddItem] Individual fields:', {
			typeLabelLong: item.typeLabelLong,
			sizeLabel: item.sizeLabel,
			brandCode: item.brandCode,
			firestoreId: item.firestoreId,
		});

		// Debug: Check if all required fields are present and not empty
		const missingFields = [];
		if (!item.typeLabelLong) missingFields.push('typeLabelLong');
		if (!item.sizeLabel) missingFields.push('sizeLabel');
		if (!item.brandCode) missingFields.push('brandCode');
		// Note: firestoreId is optional - not checking for it
		if (!userUid) missingFields.push('userUid');

		if (missingFields.length > 0) {
			console.error('[AddItem] Missing required fields:', missingFields);
			throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
		}

		const requestPayload: AddItemRequest = {
			user_uid: userUid,
			type_label_long: item.typeLabelLong,
			size_label: item.sizeLabel,
			brand_code: item.brandCode,
			fitting_code: 0, // ✅ default hardcoded
			fitting_measurements: [], // ✅ default hardcoded
		};

		// Only add firestore_id if it exists and is not empty
		if (item.firestoreId && item.firestoreId.trim() !== '') {
			requestPayload.firestore_id = item.firestoreId;
		}

		console.debug('[AddItem] Request payload:', requestPayload);
		console.debug('[AddItem] Flattened structure (no nested item):', requestPayload);
		console.debug('[AddItem] API URL:', `${API_BASE_URL}cf/user_add_item`);

		const response = await fetch(`${API_BASE_URL}cf/user_add_item`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${authToken}`,
			},
			body: JSON.stringify(requestPayload),
		});

		console.debug('[AddItem] Response status:', response.status);
		console.debug('[AddItem] Response headers:', Object.fromEntries(response.headers.entries()));

		if (!response.ok) {
			const errorText = await response.text();
			console.error('[AddItem] API Error Response:', errorText);
			let errorData;
			try {
				errorData = JSON.parse(errorText);
			} catch (e) {
				errorData = { detail: errorText };
			}
			console.error('[AddItem] API Error:', errorData);
			console.error('[AddItem] Request that failed:', {
				url: `${API_BASE_URL}cf/user_add_item`,
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${authToken.substring(0, 20)}...`,
				},
				body: requestPayload,
			});
			throw new Error(errorData.detail || errorData.message || 'Failed to add item');
		}

		const result: AddItemResponse = await response.json();
		console.debug('[AddItem] API Response:', result);
		
		// Check if success field exists and is false
		if (result.success === false) {
			throw new Error(`Failed to add item: ${result.message || 'Unknown error'}`);
		}

		console.debug('✅ [AddItem] Successfully added item to user profile');
		return result;
	} catch (error) {
		console.error('Error adding item:', error);
		throw new Error('Failed to add item');
	}
};
